import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import {
  Search, Loader2, CheckCircle2, AlertTriangle, Zap, Share2, ArrowRight,
  Rocket, Compass, ShieldCheck, TrendingUp, ClipboardList, BookOpen,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_ICONS = {
  build: Rocket, be_found: Compass, build_trust: ShieldCheck, convert_grow: TrendingUp,
};
const OUTCOME_COLORS = {
  build: "bg-orange-50 text-orange-700 ring-orange-200",
  be_found: "bg-sky-50 text-sky-700 ring-sky-200",
  build_trust: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  convert_grow: "bg-indigo-50 text-indigo-700 ring-indigo-200",
};
const SEV_TONE = {
  critical: "bg-rose-100 text-rose-800 ring-rose-300",
  major: "bg-amber-100 text-amber-800 ring-amber-300",
  minor: "bg-slate-100 text-slate-700 ring-slate-300",
  info: "bg-slate-50 text-slate-500 ring-slate-200",
};

export default function GrowthAudit() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [industry, setIndustry] = useState("other");
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(0);
  const [savedId, setSavedId] = useState(null);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    axios.get(`${API}/growth-audit/meta/industries`)
      .then((r) => setIndustries(r.data.industries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (auditId) {
      axios.get(`${API}/growth-audit/${auditId}`)
        .then((r) => { setReport(r.data.report); setSavedId(auditId); setUrl(r.data.url); })
        .catch(() => toast.error("Audit not found"));
    }
  }, [auditId]);

  const runAudit = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!url.trim()) return;
    setRunning(true); setReport(null); setProgress(5);
    // Fake progress ticker while we wait
    const stages = [
      [10, "Reaching your site…"], [22, "Reading HTML…"],
      [35, "Checking robots.txt & sitemap…"], [50, "Scanning technical SEO…"],
      [64, "Looking for schema & AI signals…"], [78, "Testing conversion paths…"],
      [90, "Grading accessibility…"],
    ];
    let ix = 0;
    const int = setInterval(() => {
      if (ix < stages.length) { setProgress(stages[ix][0]); ix++; }
    }, 700);
    try {
      const r = await axios.post(`${API}/growth-audit/run`,
        { url: url.trim(), email: email.trim(), industry });
      clearInterval(int); setProgress(100);
      setReport(r.data.report); setSavedId(r.data.audit_id);
      navigate(`/growth-audit/${r.data.audit_id}`, { replace: true });
      toast.success("Audit complete");
    } catch (err) {
      clearInterval(int); setProgress(0);
      toast.error(err?.response?.data?.detail || "Could not run the audit");
    } finally { setRunning(false); }
  }, [url, email, industry, navigate]);

  const copyShare = async () => {
    if (!savedId) return;
    const link = `${window.location.origin}/growth-audit/${savedId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Share link copied");
    } catch { toast.error("Copy failed"); }
  };

  const sendToTaskCenter = async () => {
    if (!savedId) return;
    const token = localStorage.getItem("wt_token");
    if (!token) {
      toast.info("Sign in to add these fixes to your Task Center");
      navigate(`/login?redirect=/growth-audit/${savedId}`);
      return;
    }
    setSeeding(true);
    try {
      const { data } = await axios.post(
        `${API}/growth-audit/tasks/from-audit/${savedId}`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data.created
        ? `${data.created} task${data.created !== 1 ? "s" : ""} added to your Task Center`
        : "Everything is already in your Task Center");
      navigate("/growth-tasks");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to Task Center");
    } finally { setSeeding(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="growth-audit-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">WebsiteTech · Growth System</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Free Growth Audit</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Enter your website. We scan the same signals search engines and AI answer engines use, then hand you a prioritized to-do list — usually done in under a minute.
        </p>

        <form onSubmit={runAudit} className="mt-6 bg-white ring-1 ring-slate-200 rounded-xl p-4 sm:p-5 grid gap-3"
              data-testid="growth-audit-form">
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={url} onChange={(e) => setUrl(e.target.value)}
                    required autoFocus type="text"
                    placeholder="https://yourbusiness.com"
                    className="flex-1 rounded-lg ring-1 ring-slate-200 focus:ring-slate-500 outline-none px-3 py-2 text-sm"
                    data-testid="growth-audit-url" />
            <select value={industry} onChange={(e) => setIndustry(e.target.value)}
                     className="rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm"
                     data-testid="growth-audit-industry">
              {industries.map((i) => <option key={i.code} value={i.code}>{i.name}</option>)}
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={email} onChange={(e) => setEmail(e.target.value)}
                    type="email" placeholder="Your email (optional — for the share link)"
                    className="flex-1 rounded-lg ring-1 ring-slate-200 focus:ring-slate-500 outline-none px-3 py-2 text-sm"
                    data-testid="growth-audit-email" />
            <button type="submit" disabled={running || !url.trim()}
                     className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-40"
                     data-testid="growth-audit-run">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {running ? "Scanning…" : "Run Growth Audit"}
            </button>
          </div>
          {running && (
            <div className="mt-1" data-testid="growth-audit-progress">
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Scanning… {progress}%</div>
            </div>
          )}
        </form>

        {report && (
          <section className="mt-8" data-testid="growth-audit-report">
            <div className="rounded-xl bg-white ring-1 ring-slate-200 p-5 sm:p-6">
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    Report for {new URL(url.startsWith("http") ? url : "https://" + url).hostname}
                  </div>
                  <div className="text-4xl sm:text-5xl font-extrabold text-slate-900 mt-1">
                    {report.overall_score}
                    <span className="text-lg text-slate-400 font-bold">/100</span>
                  </div>
                  <p className="text-slate-700 mt-2 max-w-2xl">{report.summary_text}</p>
                </div>
                {savedId && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button onClick={sendToTaskCenter} disabled={seeding}
                            className="text-sm inline-flex items-center gap-1 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 disabled:opacity-50 font-bold"
                            data-testid="growth-audit-send-to-tasks">
                      {seeding
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <ClipboardList className="w-4 h-4" />}
                      Add to Task Center
                    </button>
                    <button onClick={copyShare}
                            className="text-sm inline-flex items-center gap-1 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 px-3 py-2"
                            data-testid="growth-audit-share">
                      <Share2 className="w-4 h-4" /> Copy share link
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="growth-audit-scores">
                {["build", "be_found", "build_trust", "convert_grow"].map((o) => {
                  const Icon = OUTCOME_ICONS[o];
                  return (
                    <div key={o}
                         className={`rounded-lg ring-1 ${OUTCOME_COLORS[o]} p-3 text-center`}
                         data-testid={`growth-audit-score-${o}`}>
                      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold">
                        <Icon className="w-3.5 h-3.5" /> {report.outcomes[o]}
                      </div>
                      <div className="text-3xl font-extrabold mt-1">{report.scores[o]}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Bucket title="Urgent fixes" icon={AlertTriangle}
                     items={report.urgent_fixes} tone="rose"
                     testid="growth-audit-urgent" />
            <Bucket title="Quick wins" icon={Zap}
                     items={report.quick_wins} tone="amber"
                     testid="growth-audit-quickwins" />
            <Bucket title="Longer-term opportunities" icon={CheckCircle2}
                     items={report.longer_term} tone="slate"
                     testid="growth-audit-longer" />

            {report.industry_kit && (
              <section className="mt-6 rounded-xl bg-gradient-to-br from-orange-50 to-white ring-1 ring-orange-200 p-5"
                       data-testid="growth-audit-industry-kit">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Industry Kit</div>
                    <div className="font-bold text-slate-900 text-lg mt-0.5">
                      Recommended site map for {report.industry_kit.name}
                    </div>
                    <p className="text-sm text-slate-700 mt-1">{report.industry_kit.summary}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {report.industry_kit.recommended_pages.map(p => (
                        <span key={p.slug}
                              className="text-[11px] rounded-full bg-white ring-1 ring-slate-200 px-2 py-1 text-slate-700 font-mono">
                          /{p.slug}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4">
                      <Link to={`/industry-kits/${report.industry_kit.code}`}
                            className="inline-flex items-center gap-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold"
                            data-testid="growth-audit-open-kit">
                        Open full kit <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <div className="mt-8 rounded-xl bg-slate-900 text-white p-5 sm:p-6 flex flex-wrap items-center gap-4 justify-between"
                 data-testid="growth-audit-cta">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">Book a growth call</div>
                <div className="text-xl font-bold mt-1">Want us to fix the urgent items for you?</div>
                <div className="text-slate-300 text-sm mt-1">30-minute call · no obligation · we walk you through the fixes.</div>
              </div>
              <Link to="/contact"
                     className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 text-sm font-bold inline-flex items-center gap-1"
                     data-testid="growth-audit-book">
                Book a call <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function Bucket({ title, icon: Icon, items, tone, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="mt-6 rounded-xl bg-white ring-1 ring-slate-200 p-5"
             data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 text-${tone}-600`} />
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <span className="text-xs text-slate-500 ml-auto">{items.length} item{items.length !== 1 ? "s" : ""}</span>
      </div>
      <ol className="space-y-3">
        {items.map((f) => (
          <li key={f.id}
              className="rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3"
              data-testid={`finding-${f.id}`}>
            <div className="flex flex-wrap items-start gap-2">
              <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ring-1 ring-inset ${SEV_TONE[f.severity] || SEV_TONE.info}`}>
                {f.severity}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold self-center">
                {f.category.replace("_", " ")}
              </span>
              <div className="w-full font-bold text-slate-900 mt-1">{f.title}</div>
              <div className="w-full text-sm text-slate-700">{f.action}</div>
              <div className="w-full text-[11px] text-slate-500 font-mono truncate">Evidence · {f.evidence}</div>
              <div className="w-full flex flex-wrap gap-3 text-[11px] text-slate-500 mt-1">
                <span>Impact: <b className="text-slate-800">{f.impact}</b></span>
                <span>Effort: <b className="text-slate-800">{f.effort}</b></span>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
